import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { clientApi } from '@/config/axios';
import type { PageDto } from '@/lib/pagination';
import { useAuth } from '@/lib/auth/AuthContext';

export interface FlowsheetScope {
  context_view_template_id: string;
}

export interface ShareLinkCreate {
  expires_in_hours: ShareLinkExpiryHours;
  scope: ShareScope;
}

export interface ShareLinkDto {
  created_at: string;
  created_by_id: string;
  expires_at: string;
  id: string;
  is_active: boolean;
  is_expired: boolean;
  is_revoked: boolean;
  last_viewed_at: string | null;
  last_viewed_ip: string | null;
  revoked_at: string | null;
  scope: ShareScope;
  share_url: string;
  token: string;
  view_count: number;
}

export type ShareLinkExpiryHours = 1 | 2 | 5 | 24;

export interface ShareScope {
  demographics: boolean;
  /** Tag IDs the recipient may see documents under. Empty array = no documents. */
  document_tag_ids: string[];
  encounters: boolean;
  flowsheet: FlowsheetScope | null;
  /** @deprecated Use document_tag_ids. Kept for backward compatibility on read. */
  generated_documents?: boolean;
  pregnancies: boolean;
  /** @deprecated Use document_tag_ids. Kept for backward compatibility on read. */
  uploaded_documents?: boolean;
}

export function useCreateShareLink(patientId: string) {
  const { organization } = useAuth();
  const orgId = organization?.organization_id;
  const qc = useQueryClient();
  return useMutation({
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['patient-shares', orgId, patientId] });
    },
    mutationFn: async (body: ShareLinkCreate) => {
      const { data } = await clientApi.post<ShareLinkDto>(base(orgId!, patientId), body);
      return data;
    },
  });
}

export function useRevokeShareLink(patientId: string) {
  const { organization } = useAuth();
  const orgId = organization?.organization_id;
  const qc = useQueryClient();
  return useMutation({
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['patient-shares', orgId, patientId] });
    },
    mutationFn: async (linkId: string) => {
      await clientApi.delete(`${base(orgId!, patientId)}/${linkId}`);
    },
  });
}

export function useShareLinks(patientId: string | undefined, params?: { status?: 'active' | 'all' }) {
  const { organization } = useAuth();
  const orgId = organization?.organization_id;
  return useQuery({
    enabled: !!orgId && !!patientId,
    queryKey: ['patient-shares', orgId, patientId, params?.status ?? 'active'],
    queryFn: async () => {
      const { data } = await clientApi.get<PageDto<ShareLinkDto>>(base(orgId!, patientId!), {
        params: { limit: 100, status: params?.status ?? 'active' },
      });
      return data;
    },
  });
}

function base(orgId: string, patientId: string) {
  return `/api/v1/organizations/${orgId}/patients/${patientId}/share-links`;
}
